import { LoadingPage } from '@resurs-internal/deposits-app-components';
import { useContext, useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router';

import { type ApplicationHandlerProps } from '@/components';
import { CountryContext } from '@/contexts';
import { isAuthenticated } from '@/helpers';
import { useApplication } from '@/hooks';
import { fetchCountries } from '@/services';
import {
  ApplicationStatus,
  type CountryTranslationResponseDTO,
} from '@/types/consumer-deposit-application-service';

import { SectionResolver } from './SectionResolver';

export const ApplicationView = () => {
  const navigate = useNavigate();
  const { applicationId, product } =
    useOutletContext() as ApplicationHandlerProps;
  const { application, isLoading, kycSurvey } = useApplication(applicationId);
  const { country } = useContext(CountryContext);
  const [countries, setCountries] = useState<CountryTranslationResponseDTO[]>(
    [],
  );
  const [hasFetchedCountries, setHasFetchedCountries] = useState(false);

  useEffect(() => {
    if (isAuthenticated()) {
      if (ApplicationStatus.PROCESSING !== application.status) {
        switch (application.status) {
          case ApplicationStatus.READY_FOR_SIGNING:
            navigate(`/resume-application/${applicationId}`);

            return;
          case ApplicationStatus.REJECTED:
            navigate('/denied');

            return;
          case ApplicationStatus.PENDING:
            navigate(`/application/${applicationId}/pending`);

            return;

          default:
            navigate('/error');

            return;
        }
      }

      if (!isLoading && product.code !== application.productCode) {
        application.setProductCode(product.code);
      }
    } else {
      navigate('/');
    }
  }, [isLoading, navigate, product.code, application, applicationId]);

  useEffect(() => {
    if (
      !hasFetchedCountries &&
      isAuthenticated() &&
      ApplicationStatus.PROCESSING === application.status
    ) {
      setHasFetchedCountries(true);
      fetchCountries()
        .then((result) => {
          setCountries(result?.countries ?? []);
        })
        .catch((error) => {
          console.error('Failed to fetch countries for KYC section:', error);
          navigate('/error');
        });
    }
  }, [hasFetchedCountries, navigate, application.status]);

  if (isLoading || ApplicationStatus.PROCESSING !== application.status)
    return <LoadingPage country={country} />;

  return (
    <SectionResolver
      application={application}
      applicationId={applicationId}
      countries={countries}
      kycSurvey={kycSurvey}
      productName={product.name}
    />
  );
};
